import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { Typography } from 'housecall-ui';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';

import { countryShortcut } from '../../api/countryShortcut';
import { AutocompleteField } from '../../components/form/AutocompleteField';
import { FormField } from '../../components/form/FormField';
import { scrollToTop } from '../../components/ScrollableContent';
import { useBookingConfirmPaymentData } from '../../hooks/useBookingConfirmationMutation';
import { useInvoice } from '../../hooks/useInvoice';
import { useOrgData } from '../../hooks/useOrgData';
import { useOnlineBookingReducer } from '../../state/useOnlineBookingReducer';
import { COUNTRY_LIST } from '../ContactDetailsStep/consts';

import { usePayByCard } from './hooks/usePayByCard';
import { HousecallCardCvcElement, HousecallCardExpiryElement, HousecallCardNumberElement } from './CardElements';
import stripePromise from './loadStripe';
import { SaveCardOnFileField } from './SaveCardOnFileField';
import { PaymentFormFieldName, PaymentFormFields } from './types';
import { onPaymentFormValidate } from './validation';

type Props = {
  isPreview?: boolean;
};

const CreditCardForm = ({ isPreview = false }: Props) => {
  const paymentData = useBookingConfirmPaymentData();
  const { mutateAsync: payByCard } = usePayByCard();
  const invoiceData = useInvoice(paymentData?.invoiceToken);
  const [stripeError, setStripeError] = useState<StripeError | null>(null);

  const setError = (error: StripeError | null) => {
    setStripeError(error);
    if (error) {
      scrollToTop();
    }
  };

  const {
    state: { contactDetails, token },
    dispatch,
    actions,
  } = useOnlineBookingReducer();
  const stripe = useStripe();
  const elements = useElements();
  const { data: orgData } = useOrgData({ token });

  const handleSubmit = async (values: PaymentFormFields) => {
    setError(null);

    if (!stripe || !elements) {
      return;
    }
    if (!isPreview && (!paymentData?.invoiceToken || !invoiceData.data)) {
      return;
    }
    const card = elements.getElement(CardNumberElement);
    if (!card) {
      return;
    }

    const cardData = {
      name: values.name,
      address_zip: values.zipCode,
      address_country: countryShortcut(values.country),
    };

    const cardToken = await stripe.createToken(card, cardData);
    const last4DigitsOfCreditCard = cardToken?.token?.card?.last4;
    if (last4DigitsOfCreditCard) {
      dispatch(
        actions.setPaymentDetails({
          last4: last4DigitsOfCreditCard,
        })
      );
    }

    if (cardToken.error) {
      setError(cardToken.error);
    } else if (isPreview) {
      dispatch(actions.setSuccess());
    } else if (paymentData?.invoiceToken && invoiceData.data) {
      await payByCard({
        invoiceToken: paymentData.invoiceToken,
        payload: {
          amount: invoiceData.data!.dueAmount,
          fixed_gratuity: 0,
          stripe_cc_token: cardToken.token.id,
          save_card_on_file: Boolean(values.saveCard),
          card_on_file_email: contactDetails.values.email,
        },
      });
    }
  };

  const initialValues = useMemo(() => {
    return {
      name: `${contactDetails.values.firstName} ${contactDetails.values.lastName}`,
      zipCode: contactDetails.values.zipCode,
      country: contactDetails.values.country,
    };
  }, [contactDetails.values]);

  const hasStripeError = Boolean(stripeError);

  return (
    <Form<PaymentFormFields>
      onSubmit={handleSubmit}
      validate={onPaymentFormValidate({ requiredCardOnFile: Boolean(orgData?.requireCardOnFile) })}
      initialValues={initialValues}
    >
      {({ handleSubmit: onSubmit }) => (
        <form id="payment-form" onSubmit={onSubmit} aria-label="Payment details">
          <Grid container spacing={16}>
            <Grid item xs={12}>
              <FormField<PaymentFormFieldName>
                name="name"
                data-testid="online-booking-contact-name"
                label="Name"
                fullWidth
                autoComplete="given-name"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <HousecallCardNumberElement errorMessage={hasStripeError} variant="filled" labelName="Card number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <HousecallCardExpiryElement errorMessage={hasStripeError} variant="filled" labelName="Exp (MM/YY)" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <HousecallCardCvcElement errorMessage={hasStripeError} variant="filled" labelName="Security Code" />
            </Grid>
            {stripeError && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  {stripeError.message}
                </Typography>
              </Grid>
            )}
            <Grid item xs={6}>
              <AutocompleteField<PaymentFormFieldName>
                label="Country"
                name="country"
                options={COUNTRY_LIST as unknown as string[]}
                ListboxProps={{
                  'data-testid': 'payment-country-list',
                }}
                InputProps={{
                  'data-testid': 'payment-country',
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormField<PaymentFormFieldName>
                name="zipCode"
                data-testid="payment-contact-zip"
                label="Zip"
                fullWidth
                autoComplete="postal-code"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <SaveCardOnFileField />
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
};

const CreditCardFormWrapper = (props: Props) => {
  return (
    <Elements stripe={stripePromise()}>
      <CreditCardForm {...props} />
    </Elements>
  );
};

export default CreditCardFormWrapper;
